import React, { useEffect, useState, useRef, useId } from 'react';
import { Drawer, Box, IconButton, Toolbar, Checkbox } from '@mui/material';
import { ChevronLeft as ChevronLeftIcon } from '@mui/icons-material';
import { ReactComponent as CloseIcon } from '../assets/Close.svg';
import { ReactComponent as ForwardIcon } from '../assets/Forward.svg';
import { ReactComponent as BackwardIcon } from '../assets/Backward.svg';
import { ReactComponent as ScreenshotIcon } from '../assets/Screenshot.svg';
import { ReactComponent as SaveIcon } from '../assets/Save.svg';
import { ReactComponent as SearchIcon } from '../assets/Search.svg';
import { ReactComponent as UndoIcon } from '../assets/Undo.svg';
import { ReactComponent as RedoIcon } from '../assets/Redo.svg';
import { ReactComponent as DriveIcon } from '../assets/Drive.svg';
import { ReactComponent as DownloadIcon } from '../assets/Download.svg';
import { handleSavePattern, applyPattern, deletePattern, csrfHeaders } from '../utils/apiUtils';

function PDFViewer({
  state,
  stateData,
  currentPage,
  totalPages,
  changePage,
  isRedactionMode,
  handleRedactStart,
  savePDF,
  isLoading,
  canvasRef,
  overlayCanvasRef,
  handleMouseDown,
  handleMouseMove,
  handleMouseUp,
  handleMouseLeave,
  handleUndo,
  handleRedo,
  currentHistoryNode,
  STATE_NOTHING,
  STATE_PDF_DISPLAYED,
  sessionsDrawerOpen,
  setSessionsDrawerOpen,
  patternsDrawerOpen,
  setPatternsDrawerOpen,
  files,
  setFiles,
  patterns,
  handleUpload,
  showPDF,
  handleDelete,
  fetchPatternsData,
  setToast,
  workflowStage = 'idle',
  piiSuggestions = {},
  suggestionsMeta = null,
  proofReport = null,
  suggestionGroups = [],
  visibleSuggestionGroups = [],
  suggestionsLoading = false,
  activeSuggestionGroupKey = null,
  setActiveSuggestionGroupKey,
  acceptSuggestionGroup,
  dismissSuggestionGroup,
  acceptAllSuggestions,
  dismissAllSuggestions,
  onDownloadProofReport,
}) {
  
  const hasRedactions = Object.keys(stateData.redactions || {}).some(pageNum => 
    stateData.redactions[pageNum] && stateData.redactions[pageNum].length > 0
  );
  const drawerWidth = 350;
  const workflowSteps = ['uploading', 'processing', 'ready'];

  // Debug logging for undo/redo states
  console.log('PDFViewer render - currentHistoryNode:', currentHistoryNode);
  console.log('Has previous:', currentHistoryNode?.previous ? 'yes' : 'no');
  console.log('Has next:', currentHistoryNode?.next ? 'yes' : 'no');

  // State to track which dates are expanded
  const [expandedDates, setExpandedDates] = useState({});
  const [patternName, setPatternName] = useState('');
  // State for selected files
  const [selectedFiles, setSelectedFiles] = useState(new Set());
  // State for drag and drop
  const [isDragOver, setIsDragOver] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchIndex, setSearchIndex] = useState(0);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  const fileInputRef = useRef(null);
  const drawerUploadRef = useRef(null);
  const emptyUploadInputId = useId();
  const drawerUploadInputId = useId();

  const getWorkflowIndex = (stage) => {
    return workflowSteps.indexOf(stage);
  };

  const getPatternPreviewLabel = (pattern) => {
    if (!pattern) return 'Pattern';
    const trimmed = String(pattern).trim();
    if (!trimmed) return 'Pattern';
    return trimmed.length > 16 ? `${trimmed.slice(0, 16)}…` : trimmed;
  };

  const getGroupPreviewLabel = (group) => {
    if (!group) return 'Suggestion';
    const text = String(group.text || '').trim();
    if (!text) return group.type || 'Suggestion';
    return text.length > 42 ? `${text.slice(0, 42)}…` : text;
  };

  const getConfidenceLabel = (confidence = 0) => {
    if (confidence >= 0.95) return 'Very high confidence';
    if (confidence >= 0.85) return 'High confidence';
    if (confidence >= 0.75) return 'Medium confidence';
    return 'Review carefully';
  };

  const getGroupOccurrenceOnCurrentPage = (group) => (
    (group?.occurrences || []).find((occurrence) => String(occurrence.pageKey) === String(currentPage))
    || null
  );

  const activeSuggestionGroup = suggestionGroups.find((group) => group.groupKey === activeSuggestionGroupKey) || null;
  const activeSuggestionOccurrence = getGroupOccurrenceOnCurrentPage(activeSuggestionGroup) || null;
  const activeSuggestionBox = activeSuggestionOccurrence?.box || null;
  const suggestionGroupsToShow = Array.isArray(visibleSuggestionGroups)
    ? visibleSuggestionGroups
    : suggestionGroups;
  const groupedByType = suggestionGroupsToShow.reduce((accumulator, group) => {
    if (!accumulator[group.type]) {
      accumulator[group.type] = [];
    }
    accumulator[group.type].push(group);
    return accumulator;
  }, {});
  const groupedTypeEntries = Object.entries(groupedByType);

  const focusSuggestionGroup = (group) => {
    if (!group) return;
    setActiveSuggestionGroupKey?.(group.groupKey);
    const targetOccurrence = group.occurrences?.find((occurrence) => String(occurrence.pageKey) === String(currentPage))
      || group.occurrences?.[0];
    const targetPage = Number(targetOccurrence?.page ?? targetOccurrence?.pageKey);
    if (Number.isFinite(targetPage) && targetPage !== currentPage) {
      changePage(targetPage - currentPage);
    }
  };

  const clearSuggestionFocus = (group) => {
    if (!group || activeSuggestionGroupKey === group.groupKey) {
      setActiveSuggestionGroupKey?.(null);
    }
  };

  const currentPageRedactionCount = stateData.redactions?.[currentPage]?.length || 0;
  const scanLimitSummary = suggestionsMeta
    ? 'Scanning English and Arabic PII fields…'
    : 'Scanning document for English and Arabic PII fields…';
  const scanWarningText = suggestionsMeta?.warnings?.[0] || '';
  const totalSuggestionOccurrenceCount = suggestionGroupsToShow.reduce((count, group) => count + (group?.count || 0), 0);
  const proofRemainingTotal = proofReport?.remaining?.total || 0;
  const proofMetadataCount = proofReport?.metadata?.count || 0;
  const proofAttachmentCount = proofReport?.attachments?.count || 0;
  const proofLayerCount = proofReport?.layers?.count || 0;
  const proofAnnotationCount = proofReport?.annotations?.count || 0;
  const toAppRelativePath = (value) => {
    const rawPath = String(value || '').replace(/\\/g, '/');
    const mediaIndex = rawPath.lastIndexOf('/media/');
    if (mediaIndex !== -1) {
      return rawPath.slice(mediaIndex + '/media/'.length);
    }
    if (rawPath.startsWith('/')) {
      return rawPath.replace(/^\/+/, '');
    }
    return rawPath;
  };

  const handleDrawerClose = () => {
    setSessionsDrawerOpen(false);
  };

  const downloadProofReport = () => {
    if (!proofReport) return;
    const payload = {
      exported_at: new Date().toISOString(),
      proof_report: proofReport,
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${String(proofReport?.file?.name || 'redaction-proof').replace(/\.pdf$/i, '')}-proof-report.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleDownloadProofPack = () => {
    if (onDownloadProofReport) {
      onDownloadProofReport();
      return;
    }
    downloadProofReport();
  };

  const handlePatternsDrawerClose = () => {
    setPatternsDrawerOpen(false);
  };

  // Drag and drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      if (file.type === 'application/pdf') {
        const fakeEvent = {
          preventDefault: () => {},
          target: {
            elements: {
              file: { files: [file] }
            }
          }
        };
        handleUpload(fakeEvent);
      } else {
        setToast?.({ type: 'error', message: 'Please upload only PDF files.' });
      }
    }
  };

  const handleDropzoneClick = () => {
    fileInputRef.current?.click();
  };

  const runSearch = async (query) => {
    const trimmed = (query || '').trim();
    if (!trimmed || state !== STATE_PDF_DISPLAYED || !stateData?.currentPdfId) {
      setSearchResults([]);
      return;
    }
    setIsSearching(true);
    setSearchError('');
    try {
      const res = await fetch('/api/search', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await csrfHeaders()) },
        credentials: 'include',
        body: JSON.stringify({
          file_id: stateData.currentPdfId,
          query: trimmed,
          fuzzy: true,
          threshold: 0.78,
          max_results: 12,
        }),
      });
      const data = await res.json();
      if (!res.ok || data.error) {
        throw new Error(data.error || 'Search failed');
      }
      const matches = Array.isArray(data.matches) ? data.matches : [];
      setSearchResults(matches);
      setSearchIndex(0);
      if (matches.length > 0) {
        const targetPage = matches[0].page;
        changePage(targetPage - currentPage);
        setToast?.({ type: 'success', message: `Found ${matches.length} match(es). Jumped to page ${targetPage + 1}.` });
      } else {
        setToast?.({ type: 'warn', message: 'No matches found.' });
      }
    } catch (err) {
      console.error('Search failed', err);
      setSearchError(err.message || 'Search failed');
      setToast?.({ type: 'error', message: err.message || 'Search failed' });
    } finally {
      setIsSearching(false);
    }
  };

  const goToSearchResult = (nextIndex) => {
    if (!searchResults.length) return;
    const bounded = (nextIndex + searchResults.length) % searchResults.length;
    setSearchIndex(bounded);
    const targetPage = searchResults[bounded].page;
    changePage(targetPage - currentPage);
  };

  const handleFileChange = (e) => {
    if (e.target.files.length > 0) {
      handleUpload(e);
    }
  };

  // Function to toggle date expansion
  const toggleDateExpansion = (date) => {
    setExpandedDates(prev => ({
      ...prev,
      [date]: !prev[date]
    }));
  };

  // Function to handle file selection
  const handleFileSelect = (fileId, isSelected) => {
    setSelectedFiles(prev => {
      const newSelected = new Set(prev);
      if (isSelected) {
        newSelected.add(fileId);
      } else {
        newSelected.delete(fileId);
      }
      return newSelected;
    });
  };

  const handleSelectAllForDate = (dateFiles, isSelected) => {
    setSelectedFiles(prev => {
      const newSelected = new Set(prev);
      dateFiles.forEach(file => {
        if (isSelected) {
          newSelected.add(file.id);
        } else {
          newSelected.delete(file.id);
        }
      });
      return newSelected;
    });
  };

  const areAllFilesSelectedForDate = (dateFiles) => {
    return dateFiles.every(file => selectedFiles.has(file.id));
  };

  const areSomeFilesSelectedForDate = (dateFiles) => {
    return dateFiles.some(file => selectedFiles.has(file.id)) && !areAllFilesSelectedForDate(dateFiles);
  };

  const getTargetFiles = () => {
    if (selectedFiles.size > 0) {
      return files.filter(file => selectedFiles.has(file.id));
    }
    if (stateData?.currentPdfId) {
      const current = files.find(f => String(f.id) === String(stateData.currentPdfId));
      if (current) return [current];
      // Fallback: allow actions on the currently open file even if it isn't in the sidebar list.
      return [{
        id: stateData.currentPdfId,
        title: stateData?.pdfPath ? stateData.pdfPath.split('/').pop() : `file-${stateData.currentPdfId}.pdf`,
      }];
    }
    return [];
  };

  const handleBulkDelete = async () => {
    const targets = getTargetFiles();
    if (targets.length === 0) return;

    if (window.confirm(`Are you sure you want to delete ${targets.length} file(s)?`)) {
      try {
        // Delete files one by one
        for (const file of targets) {
          await handleDelete(file.id);
        }
        // Clear selected files after deletion
        setSelectedFiles(new Set());
        setToast && setToast({ type: 'success', message: `${targets.length} file(s) deleted.` });
      } catch (error) {
        console.error('Error during bulk delete:', error);
        setToast && setToast({ type: 'error', message: 'Delete failed. Please try again.' });
      }
    }
  };

  // Function to handle bulk save to Google Drive
  const handleBulkSaveToDrive = async () => {
    const targets = getTargetFiles();
    if (targets.length === 0) return;

    try {
      for (const file of targets) {
        await handleSaveToDrive(file);
      }
      
      // Clear selected files after saving
      setSelectedFiles(new Set());
    } catch (error) {
      console.error('Error during bulk save to Drive:', error);
    }
  };

  // Function to handle bulk download
  const handleBulkDownload = async () => {
    const targets = getTargetFiles();
    if (targets.length === 0) return;

    try {
      for (const file of targets) {
        // Fetch first so we can report failures in-app instead of browser download errors.
        const downloadUrl = new URL(`/api/redacted/id/${file.id}`, window.location.origin).toString();
        const response = await fetch(downloadUrl, {
          method: 'GET',
          credentials: 'include',
        });
        if (!response.ok) {
          throw new Error(`Could not download "${file.title || file.id}" (HTTP ${response.status})`);
        }
        const blob = await response.blob();
        const objectUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = file.title || 'download.pdf';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(objectUrl);
        
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      setSelectedFiles(new Set());
      setToast && setToast({ type: 'success', message: `${targets.length} file(s) downloaded.` });
    } catch (error) {
      console.error('Error during bulk download:', error);
      setToast && setToast({ type: 'error', message: error.message || 'Download failed. Please try again.' });
    }
  };

  const truncateFilename = (filename, maxLength = 35) => {
    const safeName = filename || 'Untitled';
    if (safeName.length <= maxLength) {
      return safeName;
    }
    return safeName.substring(0, maxLength) + '...';
  };

  const getStartOfWeek = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day;
    return new Date(d.setDate(diff));
  };

  const isCurrentWeek = (dateString) => {
    const fileDate = new Date(dateString);
    const startOfWeek = getStartOfWeek(new Date());
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    
    return fileDate >= startOfWeek && fileDate <= endOfWeek;
  };

  const handleSaveToDrive = async (file) => {
    try {
      const response = await fetch('/api/drive/save', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          file_id: file.id,
          title: file.title 
        }),
      });

      const data = await response.json();
      
      if (data.success) {
        console.log(`File "${file.title}" saved to Google Drive successfully!`);
      } else if (data.needs_reauth) {
        const frontendOrigin = encodeURIComponent(window.location.origin);
        const authPath = data.auth_url || '/api/google/login?drive=1';
        const separator = authPath.includes('?') ? '&' : '?';
        window.location.href = `${authPath}${separator}frontend_origin=${frontendOrigin}`;
      } else {
        console.error(`Failed to save to Google Drive: ${data.error || 'Unknown error'}`);
        if (data.details) {
          console.error('Drive save error details:', data.details);
        }
      }
    } catch (error) {
      console.error('Error saving to Google Drive:', error);
    }
  };

  const sessionsByDate = files.reduce((acc, file) => {
    const dateSource = file.uploadDate || (file.metadata && file.metadata.created_at) || new Date().toISOString();
    const date = new Date(dateSource).toLocaleDateString();
    if (!acc[date]) {
      acc[date] = [];
    }
    acc[date].push(file);
    return acc;
  }, {});

  const recentSessions = {};
  const historySessions = {};

  Object.entries(sessionsByDate).forEach(([date, files]) => {
    if (isCurrentWeek(date)) {
      recentSessions[date] = files;
    } else {
      historySessions[date] = files;
    }
  });

  const SessionsContent = () => (
    <Box sx={{ width: drawerWidth, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <h3 className="font-medium text-lg">Sessions</h3>
          <IconButton onClick={handleDrawerClose} size="small" aria-label="Close sessions panel">
            <ChevronLeftIcon />
          </IconButton>
        </Box>
        {/* Compact upload action: the large upload zone lives in the center. */}
        <div className="sessions-upload-card">
          <div className="sessions-upload-copy">
            <strong>Upload a new PDF</strong>
            <span>Use the central drop zone or this quick picker.</span>
          </div>
          <button
            type="button"
            className="sessions-upload-button"
            onClick={() => drawerUploadRef.current?.click()}
            disabled={isLoading}
          >
            Upload PDF
          </button>
          <input
            ref={drawerUploadRef}
            id={drawerUploadInputId}
            className="sr-only"
            type="file"
            name="file"
            accept=".pdf,application/pdf"
            disabled={isLoading}
            onChange={handleUpload}
            aria-label="Upload PDF from sessions panel"
          />
        </div>
      </Box>
      
      {/* Scrollable sessions list */}
      <Box sx={{ flex: 1, overflow: 'auto', px: 2 }}>
        <div className="sessions-list">
          <h3 className="font-medium text-lg mb-2">Recent Sessions</h3>
          {Object.entries(recentSessions).length > 0 ? (
            // Sort dates in descending order (most recent first) before mapping
            Object.entries(recentSessions)
              .sort(([dateA], [dateB]) => {
                // Convert string dates back to Date objects for comparison
                return new Date(dateB) - new Date(dateA);
              })
              .map(([date, sessionFiles]) => (
                <div key={date} className="session-group">
                  <div 
                    className="session-date" 
                    onClick={() => toggleDateExpansion(date)}
                    style={{ 
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <Checkbox
                      size="small"
                      checked={areAllFilesSelectedForDate(sessionFiles)}
                      indeterminate={areSomeFilesSelectedForDate(sessionFiles)}
                      onChange={(e) => handleSelectAllForDate(sessionFiles, e.target.checked)}
                      onClick={(e) => e.stopPropagation()}
                      sx={{ 
                        color: 'white',
                        '&.Mui-checked': { color: 'var(--brand-blue)' },
                        '&.MuiCheckbox-indeterminate': { color: 'var(--brand-blue)' }
                      }}
                    />
                    <div style={{ flex: 1 }}>
                      {date} ({sessionFiles.length} PDF{sessionFiles.length !== 1 ? 's' : ''})
                      <span style={{ marginLeft: '8px', fontSize: '12px' }}>
                        {expandedDates[date] ? '▼' : '▶'}
                      </span>
                    </div>
                  </div>
                  {expandedDates[date] && (
                    <div className="session-files">
                      {(sessionFiles || []).filter(Boolean).map(file => (
                        <div key={file.id} className="session-file-row">
                          <Checkbox
                            size="small"
                            checked={selectedFiles.has(file.id)}
                            onChange={(e) => handleFileSelect(file.id, e.target.checked)}
                            sx={{ 
                              color: 'white',
                              '&.Mui-checked': { color: 'var(--brand-blue)' }
                            }}
                          />
                          <div 
                            className="session-file" 
                            title={file?.title || 'Untitled'}
                            onClick={() => {
                              setSelectedFiles(new Set([file.id]));
                              showPDF && showPDF(file);
                            }}
                          >
                            {truncateFilename(file?.title || 'Untitled')}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))
          ) : (
            <p className="text-sm text-gray-500">No recent sessions</p>
          )}
        </div>

        {Object.entries(historySessions).length > 0 && (
          <div className="sessions-list">
            <h3 className="font-medium text-lg mb-2">History</h3>
            {Object.entries(historySessions)
              .sort(([dateA], [dateB]) => {
                // Convert string dates back to Date objects for comparison
                return new Date(dateB) - new Date(dateA);
              })
              .map(([date, sessionFiles]) => (
                <div key={date} className="session-group">
                  <div 
                    className="session-date" 
                    onClick={() => toggleDateExpansion(date)}
                    style={{ 
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <Checkbox
                      size="small"
                      checked={areAllFilesSelectedForDate(sessionFiles)}
                      indeterminate={areSomeFilesSelectedForDate(sessionFiles)}
                      onChange={(e) => handleSelectAllForDate(sessionFiles, e.target.checked)}
                      onClick={(e) => e.stopPropagation()}
                      sx={{ 
                        color: 'white',
                        '&.Mui-checked': { color: 'var(--brand-blue)' },
                        '&.MuiCheckbox-indeterminate': { color: 'var(--brand-blue)' }
                      }}
                    />
                    <div style={{ flex: 1 }}>
                      {date} ({sessionFiles.length} PDF{sessionFiles.length !== 1 ? 's' : ''})
                      <span style={{ marginLeft: '8px', fontSize: '12px' }}>
                        {expandedDates[date] ? '▼' : '▶'}
                      </span>
                    </div>
                  </div>
                  {expandedDates[date] && (
                    <div className="session-files">
                      {(sessionFiles || []).filter(Boolean).map(file => (
                        <div key={file.id} className="session-file-row">
                          <Checkbox
                            size="small"
                            checked={selectedFiles.has(file.id)}
                            onChange={(e) => handleFileSelect(file.id, e.target.checked)}
                            sx={{ 
                              color: 'white',
                              '&.Mui-checked': { color: 'var(--brand-blue)' }
                            }}
                          />
                          <div 
                            className="session-file" 
                            title={file?.title || 'Untitled'}
                            onClick={() => {
                              setSelectedFiles(new Set([file.id]));
                              showPDF && showPDF(file);
                            }}
                          >
                            {truncateFilename(file?.title || 'Untitled')}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
          </div>
        )}
      </Box>
      
      {/* Fixed action icons at bottom */}
      <Box sx={{ p: 2, borderTop: '1px solid #555', flexShrink: 0 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2
        }}>
          {/* Selected files text above */}
          <div className="selected-files-text">
            {selectedFiles.size === 0 ? (
              <span>No files selected</span>
            ) : (
              <span>{selectedFiles.size} file{selectedFiles.size !== 1 ? 's' : ''} selected</span>
            )}
          </div>
          
          {/* Action icons centered */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 3 
          }}>
            {/** Determine if actions should be active (selected files or open PDF) */}
            {(() => {
              const hasCurrent = Boolean(stateData?.currentPdfId);
              const hasSelected = selectedFiles.size > 0;
              const canAct = hasSelected || hasCurrent;
              const actionStyle = {
                opacity: canAct ? 1 : 0.3,
                cursor: canAct ? 'pointer' : 'not-allowed'
              };
              return (
                <>
                  <button
                    type="button"
                    className="bottom-action-icon"
                    aria-label={
                      hasSelected
                        ? `Save ${selectedFiles.size} selected file(s) to Google Drive`
                        : hasCurrent
                          ? 'Save current file to Google Drive'
                          : 'Select or open a file first'
                    }
                    onClick={() => {
                      if (canAct) {
                        handleBulkSaveToDrive();
                        return;
                      }
                      setToast && setToast({ type: 'info', message: 'Open or select a file first.' });
                    }}
                    title={
                      hasSelected
                        ? `Save ${selectedFiles.size} selected file(s) to Google Drive`
                        : hasCurrent
                          ? 'Save current file to Google Drive'
                          : 'Select or open a file first'
                    }
                    style={actionStyle}
                    disabled={!canAct}
                  >
                    <DriveIcon />
                    {selectedFiles.size > 0 && (
                      <span className="action-count">{selectedFiles.size}</span>
                    )}
                  </button>
                  <button
                    type="button"
                    className="bottom-action-icon"
                    aria-label={
                      hasSelected
                        ? `Download ${selectedFiles.size} selected file(s)`
                        : hasCurrent
                          ? 'Download current file'
                          : 'Select or open a file first'
                    }
                    onClick={() => {
                      if (canAct) {
                        handleBulkDownload();
                        return;
                      }
                      setToast && setToast({ type: 'info', message: 'Open or select a file first.' });
                    }}
                    title={
                      hasSelected
                        ? `Download ${selectedFiles.size} selected file(s)`
                        : hasCurrent
                          ? 'Download current file'
                          : 'Select or open a file first'
                    }
                    style={actionStyle}
                    disabled={!canAct}
                  >
                    <DownloadIcon />
                    {selectedFiles.size > 0 && (
                      <span className="action-count">{selectedFiles.size}</span>
                    )}
                  </button>
                  <button
                    type="button"
                    className="bottom-action-icon"
                    aria-label={
                      hasSelected
                        ? `Delete ${selectedFiles.size} selected file(s)`
                        : hasCurrent
                          ? 'Delete current file'
                          : 'Select or open a file first'
                    }
                    onClick={() => {
                      if (canAct) {
                        handleBulkDelete();
                        return;
                      }
                      setToast && setToast({ type: 'info', message: 'Open or select a file first.' });
                    }}
                    title={
                      hasSelected
                        ? `Delete ${selectedFiles.size} selected file(s)`
                        : hasCurrent
                          ? 'Delete current file'
                          : 'Select or open a file first'
                    }
                    style={actionStyle}
                    disabled={!canAct}
                  >
                    <CloseIcon />
                    {selectedFiles.size > 0 && (
                      <span className="action-count">{selectedFiles.size}</span>
                    )}
                  </button>
                </>
              );
            })()}
          {/*
            <div 
              className="bottom-action-icon"
              onClick={handleBulkSaveToDrive}
              title={`Save ${selectedFiles.size} selected file(s) to Google Drive`}
              style={{
                opacity: selectedFiles.size === 0 ? 0.3 : 1,
                cursor: selectedFiles.size === 0 ? 'not-allowed' : 'pointer'
              }}
            >
              <DriveIcon />
              {selectedFiles.size > 0 && (
                <span className="action-count">{selectedFiles.size}</span>
              )}
            </div>
            <div 
              className="bottom-action-icon"
              onClick={handleBulkDownload}
              title={`Download ${selectedFiles.size} selected file(s)`}
              style={{
                opacity: selectedFiles.size === 0 ? 0.3 : 1,
                cursor: selectedFiles.size === 0 ? 'not-allowed' : 'pointer'
              }}
            >
              <DownloadIcon />
              {selectedFiles.size > 0 && (
                <span className="action-count">{selectedFiles.size}</span>
              )}
            </div>
            <div 
              className="bottom-action-icon"
              onClick={handleBulkDelete}
              title={`Delete ${selectedFiles.size} selected file(s)`}
              style={{
                opacity: selectedFiles.size === 0 ? 0.3 : 1,
                cursor: selectedFiles.size === 0 ? 'not-allowed' : 'pointer'
              }}
            >
              <CloseIcon />
              {selectedFiles.size > 0 && (
                <span className="action-count">{selectedFiles.size}</span>
              )}
            </div>
          */}  
          </Box>
        </Box>
      </Box>
    </Box>
  );

  const PatternsContent = () => (
      <Box sx={{ width: drawerWidth, p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <h3 className="font-medium text-lg">Redaction Patterns</h3>
          <IconButton onClick={handlePatternsDrawerClose} size="small" aria-label="Close redaction patterns panel">
                      <ChevronLeftIcon />
          </IconButton>
        </Box>

        <div className="patterns-list" style={{ marginBottom: '18px' }}>
        {proofReport && (
          <div
            className="proof-report-card"
            style={{
              marginBottom: '16px',
              padding: '14px',
              borderRadius: '16px',
              border: '1px solid rgba(94, 129, 244, 0.22)',
              background: 'linear-gradient(180deg, rgba(13, 24, 45, 0.95), rgba(10, 16, 31, 0.9))',
            }}
          >
            <div className="patterns-help" style={{ marginBottom: '8px' }}>
              <div>
                <h3 className="font-medium text-lg mb-1">Pre-flight safety scan</h3>
                <p className="text-xs text-gray-400">{proofReport.message}</p>
              </div>
              <span className="patterns-count" aria-live="polite">
                {proofReport.status === 'clean' ? 'Review complete' : 'Needs review'}
              </span>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '10px' }}>
              <span className="trust-badge">Remaining hits: {proofRemainingTotal}</span>
              <span className="trust-badge">Metadata keys: {proofMetadataCount}</span>
              <span className="trust-badge">Attachments: {proofAttachmentCount}</span>
              <span className="trust-badge">Layers: {proofLayerCount}</span>
              <span className="trust-badge">Annotations: {proofAnnotationCount}</span>
              <span className="trust-badge">Pages scanned: {proofReport.file?.page_count || totalPages || 0}</span>
              {proofReport.sanitization && <span className="trust-badge">Release sanitization complete</span>}
            </div>
            {Array.isArray(proofReport.checks) && proofReport.checks.length > 0 && (
              <div style={{ display: 'grid', gap: '8px', marginBottom: '10px' }}>
                {proofReport.checks.map((check) => (
                  <div
                    key={check.key}
                    style={{
                      padding: '10px 12px',
                      borderRadius: '12px',
                      background: 'rgba(255,255,255,0.04)',
                      border: '1px solid rgba(255,255,255,0.06)',
                    }}
                  >
                    <strong style={{ display: 'block', marginBottom: '2px' }}>
                      {check.label} · {check.status === 'pass' ? 'Clear' : 'Review'}
                    </strong>
                    <span className="text-xs text-gray-400">{check.detail}</span>
                  </div>
                ))}
              </div>
            )}
            {Array.isArray(proofReport.remaining?.examples) && proofReport.remaining.examples.length > 0 && (
              <div style={{ display: 'grid', gap: '8px' }}>
                {proofReport.remaining.examples.slice(0, 3).map((example, index) => (
                  <div
                    key={`${example.page}-${example.text}-${index}`}
                    style={{
                      padding: '10px 12px',
                      borderRadius: '12px',
                      background: 'rgba(255,255,255,0.04)',
                      border: '1px solid rgba(255,255,255,0.06)',
                    }}
                  >
                    <strong style={{ display: 'block', marginBottom: '2px' }}>
                      Page {example.page} · {example.type}
                    </strong>
                    <span className="text-xs text-gray-400">
                      {example.text}
                      {' · '}
                      {example.source === 'ner' ? 'NER' : 'Regex'}
                      {' · '}
                      {getConfidenceLabel(example.confidence || 0)}
                    </span>
                  </div>
                ))}
              </div>
            )}
            <div className="mt-3" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <button type="button" className="qs-btn" onClick={handleDownloadProofPack}>
                Download proof pack
              </button>
              <button type="button" className="qs-btn ghost" onClick={downloadProofReport}>
                Download JSON
              </button>
            </div>
          </div>
        )}
        <div className="patterns-help">
          <div>
            <h3 className="font-medium text-lg mb-1">Suggested Redactions</h3>
            <p className="text-xs text-gray-400">Reviewable English and Arabic PII, plus native PDF signatures and stamps.</p>
          </div>
          <span className="patterns-count" aria-live="polite">
            {suggestionsLoading ? 'Scanning…' : `${suggestionGroupsToShow.length} groups · ${totalSuggestionOccurrenceCount} occurrences`}
          </span>
        </div>
        {scanWarningText && (
          <p className="text-xs text-gray-400" style={{ marginTop: '4px' }}>
            {scanWarningText}
          </p>
        )}
        {groupedTypeEntries.length > 0 ? (
          <div className="suggestion-type-sections" style={{ display: 'flex', flexDirection: 'column', gap: '14px', maxHeight: '52vh', overflowY: 'auto', paddingRight: '4px' }}>
            {groupedTypeEntries.map(([type, groups]) => (
              <section key={type} className="suggestion-type-section">
                <div className="patterns-help" style={{ marginBottom: '8px' }}>
                  <div>
                    <h4 className="font-medium text-sm mb-1">{type.replace(/_/g, ' ')}</h4>
                    <p className="text-xs text-gray-400">
                      {groups.length} grouped item{groups.length === 1 ? '' : 's'}
                    </p>
                  </div>
                  <span className="patterns-count" aria-live="polite">
                    {groups.reduce((count, group) => count + (group.count || 0), 0)} hits
                  </span>
                </div>
                <div className="saved-pattern-list">
                  {groups.map((group) => {
                    const isActive = activeSuggestionGroupKey === group.groupKey;
                    return (
                      <div
                        key={group.groupKey}
                        className="saved-pattern-card"
                        style={{
                          outline: isActive ? '1px solid rgba(79, 149, 255, 0.65)' : 'none',
                          boxShadow: isActive ? '0 0 0 1px rgba(79, 149, 255, 0.35), 0 12px 28px rgba(15, 23, 42, 0.18)' : undefined,
                        }}
                        onMouseEnter={() => setActiveSuggestionGroupKey?.(group.groupKey)}
                        onMouseLeave={() => clearSuggestionFocus(group)}
                      >
                        <button
                          type="button"
                          className="saved-pattern-main"
                          onClick={() => focusSuggestionGroup(group)}
                          title={`Preview ${group.text} (${group.count} occurrence${group.count === 1 ? '' : 's'})`}
                        >
                          <div className="saved-pattern-preview" aria-hidden="true">
                            <span className="saved-pattern-preview-chip">{type}</span>
                          </div>
                          <div className="saved-pattern-meta">
                            <strong>{getGroupPreviewLabel(group)}</strong>
                            <span>
                              {group.count} occurrence{group.count === 1 ? '' : 's'} on {group.pages.length} page{group.pages.length === 1 ? '' : 's'}
                            </span>
                            <span className="text-xs text-gray-400">
                              {(group.sources || []).includes('ner') ? 'NER assist' : 'Regex'} · {getConfidenceLabel(group.averageConfidence || 0)}
                            </span>
                            {group.reason ? (
                              <span className="text-xs text-gray-400">{group.reason}</span>
                            ) : null}
                          </div>
                        </button>
                        <div className="saved-pattern-actions" aria-label={`Actions for ${group.type} ${group.text}`}>
                          <button
                            type="button"
                            className="qs-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              acceptSuggestionGroup?.(group);
                            }}
                            disabled={!group?.occurrences?.length}
                          >
                            Accept all
                          </button>
                          <button
                            type="button"
                            className="qs-btn ghost"
                            onClick={(e) => {
                              e.stopPropagation();
                              dismissSuggestionGroup?.(group);
                            }}
                            disabled={!group?.occurrences?.length}
                          >
                            Dismiss all
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500">
            {suggestionsLoading
              ? scanLimitSummary
              : suggestionGroups.length > 0
                ? 'No suggestions match the active filters.'
                : 'No suggestions on this page yet.'}
          </p>
        )}
        <div className="mt-3" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button
            type="button"
            className="qs-btn"
            onClick={() => acceptAllSuggestions?.()}
            disabled={suggestionGroups.length === 0}
          >
            Accept all
          </button>
          <button
            type="button"
            className="qs-btn ghost"
            onClick={() => dismissAllSuggestions?.()}
            disabled={suggestionGroups.length === 0}
          >
            Dismiss all
          </button>
        </div>
      </div>
      
      <div className="patterns-list">
        <div className="patterns-help">
          <div>
            <h3 className="font-medium text-lg mb-1">Saved Patterns</h3>
            <p className="text-xs text-gray-400">
              1. Draw a box on the current page and save it. 2. Click a saved pattern to apply it. 3. Use × to remove it.
            </p>
          </div>
          <span className="patterns-count" aria-live="polite">
            {patterns?.length || 0} saved
          </span>
        </div>
        {patterns && patterns.length > 0 ? (
          <div className="saved-pattern-list">
            {patterns.map((pattern) => {
              const previewLabel = getPatternPreviewLabel(pattern);
              const hasReadablePreview = previewLabel.length > 2;

              return (
                <div key={pattern} className="saved-pattern-card">
                  <button
                    type="button"
                    className="saved-pattern-main"
                    disabled={!stateData.currentPdfId || !stateData.pdfPath}
                    title={
                      !stateData.currentPdfId || !stateData.pdfPath
                        ? 'Open a PDF before applying saved patterns.'
                        : `Apply ${previewLabel} to the current document.`
                    }
                    onClick={async () => {
                      if (applyPattern) {
                        const result = await applyPattern(pattern, stateData, currentPage);
                        if (result && result.success && result.data) {
                          const filename = result.data.filename || (result.data.redactedPdfUrl || '').split('/').pop();
                          const relativePath = toAppRelativePath(
                            result.data.file_info?.path
                            || result.data.redactedPdfUrl
                            || result.data.relative_pdf_path
                            || result.data.download_url
                            || `redacted/0/${filename}`
                          );

                          const newFile = {
                            id: result.data.file_info?.id || result.data.id || result.data.file_id || stateData.currentPdfId || null,
                            title: filename,
                            path: relativePath,
                            pages: stateData.pages,
                          };
                          setFiles((prevFiles) => [newFile, ...prevFiles]);
                          if (showPDF) showPDF(newFile);
                          setToast?.({ type: 'success', message: `Applied pattern “${previewLabel}”.` });
                        } else {
                          const message = (result && result.error) ? result.error : 'Failed to apply pattern';
                          setToast?.({ type: 'error', message });
                        }
                      }
                    }}
                  >
                    <div className="saved-pattern-preview" aria-hidden="true">
                      {hasReadablePreview ? (
                        <span className="saved-pattern-preview-chip">{previewLabel.slice(0, 2)}</span>
                      ) : (
                        <span className="saved-pattern-preview-placeholder">∅</span>
                      )}
                    </div>
                    <div className="saved-pattern-meta">
                      <strong>{previewLabel}</strong>
                      <span>Saved pattern</span>
                    </div>
                  </button>
                  <button
                    type="button"
                    className="saved-pattern-delete"
                    aria-label={`Delete saved pattern ${previewLabel}`}
                    title={`Delete ${previewLabel}`}
                    onClick={async (event) => {
                      event.stopPropagation();
                      const result = await deletePattern(pattern);
                      if (result.success) {
                        setToast?.({ type: 'success', message: `Deleted pattern “${previewLabel}”.` });
                        await fetchPatternsData?.();
                      } else {
                        setToast?.({ type: 'error', message: result.error || 'Failed to delete pattern.' });
                      }
                    }}
                  >
                    ×
                  </button>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-sm text-gray-500">No patterns saved yet</p>
        )}
      </div>
      
      <div className="mt-4 flex gap-2 items-center">
        <input
          type="text"
          placeholder="Pattern name"
          value={patternName}
          onChange={e => setPatternName(e.target.value)}
          className="pattern-name-input"
          aria-label="Pattern name"
        />
        <button
          className="pattern-create-button"
          type="button"
          disabled={!patternName.trim() || currentPageRedactionCount === 0}
          title={
            currentPageRedactionCount === 0
              ? 'Draw at least one redaction box on this page before saving.'
              : 'Save the current page selection as a reusable pattern.'
          }
          onClick={async () => {
            if (patternName.trim()) {
              const res = await handleSavePattern(patternName.trim(), stateData, currentPage);
              if (res && res.success !== false) {
                setToast?.({ type: 'success', message: `Pattern “${patternName.trim()}” saved.` });
                setPatternName('');
                await fetchPatternsData();
              } else {
                const message = res && res.error ? res.error : 'Failed to save pattern';
                setToast?.({ type: 'error', message });
              }
            }
          }}
        >
          Save Pattern
        </button>
      </div>
      <p className="text-xs text-gray-500 mt-1">
        Tip: draw at least a 5×5 px box before saving. If a pattern is missing or too small, you’ll see an error toast.
      </p>

      
    </Box>
  );

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('auth') === 'drive_success') {
      setTimeout(() => {
        console.log('🎉 Google Drive access granted!');
        window.history.replaceState({}, document.title, window.location.pathname);
      }, 500);
    }
  }, []);

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <div 
        className="pdf-viewer" 
        style={{ 
          position: 'relative',
          minHeight: 'calc(100vh - 224px)', 
          height: 'calc(100vh - 224px)'
        }}
      >


        {state === STATE_PDF_DISPLAYED && (
          <div 
            className="pdf-container"
            style={{
              position: 'relative',
              overflow: 'auto',
              background: 'var(--canvas-bg)',
              border: '1px solid var(--canvas-border)',
              borderRadius: '22px',
              boxShadow: 'var(--panel-shadow)',
              backdropFilter: 'blur(6px)',
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'center',
              padding: '36px',
              margin: '0 auto',
              width: '100%',
              maxWidth: 'min(1400px, 96vw)',
              minHeight: 'calc(100vh - 260px)',
              height: 'auto'
            }}
          >
            <div style={{ 
              position: 'relative',
              boxShadow: '0 24px 50px rgba(0,0,0,0.18), 0 0 0 1px rgba(255,255,255,0.05)',
              backgroundColor: 'white',
              margin: '0 auto',
              display: 'inline-block'
            }}>
              <canvas 
                ref={canvasRef}
                style={{
                  display: 'block',
                  maxWidth: '100%',
                  height: 'auto'
                }}
              />
              <div 
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  cursor: isRedactionMode ? 'crosshair' : 'default',
                  pointerEvents: 'auto', 
                  zIndex: 5
                }}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseLeave}
              >
                <canvas
                  ref={overlayCanvasRef}
                  className="overlay-canvas"
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    pointerEvents: 'none',
                    background: 'transparent'
                  }}
                />
              </div>
              {activeSuggestionBox && (
                <div
                  className="suggestion-layer"
                  aria-label="Active suggestion highlight"
                  style={{
                    position: 'absolute',
                    inset: 0,
                    zIndex: 6,
                    pointerEvents: 'none',
                  }}
                >
                  <div
                    className="suggestion-box"
                    style={{
                      position: 'absolute',
                      left: `${Math.max(0, Math.min(1, activeSuggestionBox.x[0])) * 100}%`,
                      top: `${Math.max(0, Math.min(1, activeSuggestionBox.y[0])) * 100}%`,
                      width: `${Math.max(0, (Math.max(0, Math.min(1, activeSuggestionBox.x[1])) - Math.max(0, Math.min(1, activeSuggestionBox.x[0])))) * 100}%`,
                      height: `${Math.max(0, (Math.max(0, Math.min(1, activeSuggestionBox.y[1])) - Math.max(0, Math.min(1, activeSuggestionBox.y[0])))) * 100}%`,
                      border: '2px dashed rgba(111, 175, 255, 0.98)',
                      background: 'rgba(66, 133, 244, 0.10)',
                      borderRadius: '8px',
                      boxSizing: 'border-box',
                      boxShadow: '0 0 0 1px rgba(8, 17, 34, 0.35)',
                    }}
                  >
                    <span
                      style={{
                        fontSize: '11px',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        background: 'rgba(8, 17, 34, 0.7)',
                        borderRadius: '999px',
                        padding: '2px 8px',
                        color: '#dfeaff',
                      }}
                    >
                      {activeSuggestionGroup?.type || 'Suggestion'} · {getGroupPreviewLabel(activeSuggestionGroup)}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        {state === STATE_NOTHING && (
          <div 
            className={`upload-dropzone ${isDragOver ? 'drag-over' : ''} ${isLoading ? 'loading' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            style={{
              position: 'absolute',
              top: '-1.5rem',
              left: '-1.5rem',
              right: '-1.5rem',
              bottom: '-1.5rem',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              border: '2px dashed var(--canvas-border)',
              borderRadius: '18px',
              background: 'var(--canvas-bg)',
              transition: 'all 0.3s ease'
            }}
          >
            <button
              type="button"
              className="upload-dropzone-trigger"
              onClick={handleDropzoneClick}
              disabled={isLoading}
              aria-labelledby={`${emptyUploadInputId}-label`}
              aria-describedby={`${emptyUploadInputId}-help`}
              style={{
                position: 'relative',
                zIndex: 1,
                width: '100%',
                height: '100%',
                border: 'none',
                background: 'transparent',
                padding: 0,
                color: 'inherit',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer'
              }}
            >
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                height="80px" 
                viewBox="0 -960 960 960" 
                width="80px" 
                fill="var(--accent)" 
                className="upload-icon"
                aria-hidden="true"
                focusable="false"
                style={{
                  marginBottom: '24px',
                  transition: 'all 0.3s ease'
                }}
              >
                <path d="M260-160q-91 0-155.5-63T40-377q0-78 47-139t123-78q25-92 100-149t170-57q117 0 198.5 81.5T760-520q69 8 114.5 59.5T920-340q0 75-52.5 127.5T740-160H520q-33 0-56.5-23.5T440-240v-206l-64 62-56-56 160-160 160 160-56 56-64-62v206h220q42 0 71-29t29-71q0-42-29-71t-71-29h-60v-80q0-83-58.5-141.5T480-720q-83 0-141.5 58.5T280-520h-20q-58 0-99 41t-41 99q0 58 41 99t99 41h100v80H260Zm220-280Z"/>
              </svg>
              <div className="upload-text" style={{ textAlign: 'center' }}>
                {/* Guided empty-state: upload -> process -> ready. */}
                <div className="workflow-stepper" aria-label="Upload workflow status">
                  {workflowSteps.map((step) => {
                    const stepIndex = getWorkflowIndex(step);
                    const activeIndex = getWorkflowIndex(workflowStage);
                    const isComplete = activeIndex > stepIndex;
                    const isActive = activeIndex === stepIndex;
                    return (
                      <span
                        key={step}
                        className={`workflow-step ${isComplete ? 'complete' : ''} ${isActive ? 'active' : ''}`}
                      >
                        <span className="workflow-step-dot" aria-hidden="true" />
                        {step}
                      </span>
                    );
                  })}
                </div>
                <p 
                  className="upload-primary-text"
                  id={`${emptyUploadInputId}-label`}
                  style={{
                    fontSize: '24px',
                    fontWeight: '500',
                    color: '#e7eefc',
                    margin: '0 0 8px 0',
                    lineHeight: '1.2'
                  }}
                >
                  {isLoading ? 'Uploading...' : 'Drop your PDF here or click to browse'}
                </p>
                <p 
                  className="upload-secondary-text"
                  id={`${emptyUploadInputId}-help`}
                  style={{
                    fontSize: '18px',
                    color: '#a8b6cc',
                    margin: '0',
                    lineHeight: '1.2'
                  }}
                >
                  PDF only · Drag & drop or click · Up to 25 MB
                </p>
                <div className="upload-empty-hint">
                  <span>Secure upload</span>
                  <span>Processing appears here, then export becomes available.</span>
                </div>
              </div>
            </button>
            <input 
              ref={fileInputRef}
              id={emptyUploadInputId}
              className="sr-only"
              type="file" 
              name="file" 
              accept=".pdf,application/pdf" 
              required 
              disabled={isLoading}
              onChange={handleFileChange}
              aria-label="Upload PDF file"
            />
          </div>
        )}

        {/* Material-UI Persistent Drawer for Sessions */}
        <Drawer
          variant="persistent"
          anchor="left"
          open={sessionsDrawerOpen}
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
              position: 'absolute',
              top: '0',
              height: '100%',
              border: 'none',
              boxShadow: '2px 0 8px rgba(0, 0, 0, 0.15)',
              zIndex: 3,
              overflow: 'auto',
              backgroundColor: 'var(--panel)',
              color: 'white',
            },
          }}
          ModalProps={{
            container: () => document.querySelector('.pdf-viewer'),
            style: { position: 'absolute' },
            keepMounted: true
          }}
        >
          <SessionsContent />
        </Drawer>

        {/* Material-UI Persistent Drawer for Patterns */}
        <Drawer
          variant="persistent"
          anchor="right"
          open={patternsDrawerOpen}
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
              position: 'absolute',
              top: '0',
              height: '100%',
              border: 'none',
              boxShadow: '-2px 0 8px rgba(0, 0, 0, 0.15)',
              zIndex: 3,
              overflow: 'auto',
              backgroundColor: 'var(--panel)',
              color: 'white',
            },
          }}
          ModalProps={{
            container: () => document.querySelector('.pdf-viewer'),
            style: { position: 'absolute' },
            keepMounted: true
          }}
        >
          <PatternsContent />
        </Drawer>
      </div>

      {/* Bottom Toolbar with Page Controls */}
      <div
        style={{
          width: '80%', 
          margin: '0 auto', 
          position: 'relative'
        }}
      >
        <Toolbar
          sx={{
            backgroundColor: 'var(--panel)',
            borderTop: '1px solid #e0e0e0',
            boxShadow: '0 -2px 4px rgba(0, 0, 0, 0.1)',
            minHeight: '64px !important',
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            position: 'relative',
            padding: '0 24px',
            margin: 0
          }}
        >
          {/* Left side - Search */}
          <Box className="pdf-search-bar" sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
            <SearchIcon className="pdf-search-icon" aria-hidden="true" focusable="false" style={{ width: '20px', height: '20px', fill: '#B7B7B7' }} />
            <input
              type="text"
              placeholder="Search in document..."
              value={searchQuery}
              className="pdf-search-input"
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  runSearch(searchQuery);
                }
              }}
              onFocus={(e) => {
                e.target.classList.add('is-focused');
              }}
              onBlur={(e) => {
                e.target.classList.remove('is-focused');
              }}
            />
            <button
              type="button"
              onClick={() => runSearch(searchQuery)}
              disabled={isSearching}
              className="pdf-search-btn"
            >
              {isSearching ? 'Searching...' : 'Search'}
            </button>
            {searchResults.length > 1 && (
              <div className="pdf-search-nav">
                <button
                  type="button"
                  onClick={() => goToSearchResult(searchIndex - 1)}
                  className="pdf-search-nav-btn"
                >
                  Prev
                </button>
                <button
                  type="button"
                  onClick={() => goToSearchResult(searchIndex + 1)}
                  className="pdf-search-nav-btn"
                >
                  Next
                </button>
              </div>
            )}
            {searchResults.length > 0 && (
              <span className="pdf-search-count">
                {searchIndex + 1}/{searchResults.length}
              </span>
            )}
            {searchError && (
              <span className="pdf-search-error">{searchError}</span>
            )}
          </Box>

          {/* Center - Page Controls (absolutely positioned for perfect centering) */}
          <Box 
            sx={{ 
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex', 
              alignItems: 'center', 
              gap: 2 
            }}
          >
            <IconButton 
              onClick={() => changePage(-1)} 
              disabled={state !== STATE_PDF_DISPLAYED || currentPage === 0}
              size="small"
              aria-label="Previous page"
              sx={{ 
                '&:hover': {
                  backgroundColor: '#f1eee1'
                }
              }}
            >
              <BackwardIcon />
            </IconButton>
            <span style={{ fontSize: '16px', color: 'white', whiteSpace: 'nowrap', minWidth: 90, textAlign: 'center', display: 'inline-block' }}>
              {state === STATE_PDF_DISPLAYED ? `Page ${currentPage + 1} of ${totalPages}` : 'Page - of -'}
            </span>
            <IconButton 
              onClick={() => changePage(1)} 
              disabled={state !== STATE_PDF_DISPLAYED || currentPage === totalPages - 1}
              size="small"
              aria-label="Next page"
              sx={{ 
                '&:hover': {
                  backgroundColor: '#f1eee1'
                }
              }}
            >
              <ForwardIcon />
            </IconButton>
          </Box>

          {/* Right side - Action Buttons and Status */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 180, justifyContent: 'flex-end', flex: 1 }}>
            {isRedactionMode && (
              <IconButton 
                onClick={handleUndo}
                size="small"
                disabled={!currentHistoryNode || !currentHistoryNode.previous}
                aria-label="Undo redaction"
                sx={{ 
                  borderRadius: '4px',
                  '&:hover': {
                    backgroundColor: '#f1eee1'
                  },
                  '&:disabled': {
                    backgroundColor: 'transparent',
                    opacity: 0.3
                  }
                }}
                title="Undo"
              >
                <UndoIcon />
              </IconButton>
            )}
            {isRedactionMode && (
              <IconButton 
                onClick={handleRedo}
                size="small"
                disabled={!currentHistoryNode || !currentHistoryNode.next}
                aria-label="Redo redaction"
                sx={{ 
                  borderRadius: '4px',
                  '&:hover': {
                    backgroundColor: '#f1eee1'
                  },
                  '&:disabled': {
                    backgroundColor: 'transparent',
                    opacity: 0.3
                  }
                }}
                title="Redo"
              >
                <RedoIcon />
              </IconButton>
            )}
            <div className="viewer-primary-actions">
              {/* Primary redact action: keeps the review mode explicit. */}
              <button
                type="button"
                className={`viewer-primary-btn viewer-redact-btn ${isRedactionMode ? 'active' : ''}`}
                onClick={handleRedactStart}
                aria-pressed={isRedactionMode}
                disabled={state !== STATE_PDF_DISPLAYED}
                aria-label={isRedactionMode ? 'Exit redaction mode' : 'Start redaction mode'}
              >
                <ScreenshotIcon aria-hidden="true" focusable="false" />
                <span>{isRedactionMode ? 'Stop redacting' : 'Redact'}</span>
              </button>
              {/* Export action: only enables once redactions exist. */}
              <button
                type="button"
                className="viewer-primary-btn viewer-export-btn"
                onClick={savePDF}
                disabled={state !== STATE_PDF_DISPLAYED || isLoading || !hasRedactions}
                aria-label="Export redacted PDF"
              >
                <SaveIcon aria-hidden="true" focusable="false" />
                <span>{isLoading ? 'Exporting…' : 'Export PDF'}</span>
              </button>
            </div>
            <div className="viewer-status-pill" aria-live="polite">
              {workflowStage === 'uploading'
                ? 'Uploading'
                : workflowStage === 'processing'
                  ? 'Processing'
                  : workflowStage === 'ready'
                    ? 'Ready'
                    : 'Idle'}
            </div>
            <div 
              style={{ 
                width: '12px', 
                height: '12px', 
                borderRadius: '50%', 
                backgroundColor: state === STATE_PDF_DISPLAYED ? '#28a745' : '#dc3545',
                display: 'inline-block'
              }}
              title={workflowStage === 'ready' || state === STATE_PDF_DISPLAYED ? 'Ready' : 'No file selected'}
            />
          </Box>
        </Toolbar>
      </div>
    </div>
  );
}

export default PDFViewer;
